// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation), pauses on hover, focus and hidden tabs,
// and supports a sticky pause/play toggle via commandfor + command="--toggle".
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hover = false;
  #focus = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("pointerenter", () => { this.#hover = true; this.#sync(); }, { signal });
    this.addEventListener("pointerleave", () => { this.#hover = false; this.#sync(); }, { signal });
    this.addEventListener("focusin", () => { this.#focus = true; this.#sync(); }, { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#sync(), { signal });

    // The toggle button may sit outside the element; commands are dispatched on this target.
    this.addEventListener("command", (event) => {
      if (event.command !== "--toggle") return;
      this.#userPaused = !this.#userPaused;
      this.#updateToggle(event.source);
      this.#sync();
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #updateToggle(source) {
    const buttons = source ? [source] : document.querySelectorAll(`[commandfor="${this.id}"]`);
    for (const button of buttons) {
      const label = this.#userPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
      if (label) button.setAttribute("aria-label", label);
      button.setAttribute("aria-pressed", String(this.#userPaused));
      button.dataset.paused = String(this.#userPaused);
    }
  }

  #sync() {
    if (!this.#animation) return;
    const shouldPause = this.#userPaused || this.#hover || this.#focus || document.hidden;
    if (shouldPause) this.#animation.pause();
    else this.#animation.play();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless; copies are hidden from assistive tech.
    if (!track.dataset.cloned) {
      const originals = [...track.children];
      const clones = originals.map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("inert", "");
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
      track.dataset.originalCount = String(originals.length);
    }

    const count = parseInt(track.dataset.originalCount, 10) || track.children.length / 2;
    const firstClone = track.children[count];
    const first = track.children[0];
    const distance = firstClone && first
      ? Math.abs(firstClone.offsetLeft - first.offsetLeft)
      : track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    // In RTL the clones extend toward the left edge, so the travel sign flips.
    const sign = getComputedStyle(this).direction === "rtl" ? 1 : -1;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? sign * distance : 0;
    const to = reverse ? 0 : sign * distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
